import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled'

import { FooterEditEntity } from 'components/ui/footer'
import { IconPlus } from 'components/ui/icons'
import { Button, ButtonGroup } from 'components/ui/button'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { SectionHeader } from 'components/ui/section-header'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'

import { connect } from 'containers/store'
import { sharedActionCreators, SharedActionCreators } from 'containers/shared'

import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'
import { SpacesStore } from 'domains/spaces/store'
import { spacesBindableActionCreators } from 'domains/spaces/actions'
import { IssueMesages } from 'domains/org/getty/utils/validation'

import { Page } from 'generated/mos/pagination'
import { GetTourRequest } from 'generated/mos/org/getty/audioaccess'
import { UpdateTourRequest } from 'generated/mos/org/getty/audiomanagement'
import { Stop, Tour } from 'generated/mos/org/getty/audio'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { ValidationFailure } from 'generated/mos/org/getty/validation'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'

import { space } from 'helpers/style'
import { isNotNullOrUndefined, pick, pluralise } from 'helpers/core'
import { Status, statusSelector } from 'helpers/status'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { getTourSubnav } from './subnav'
import { ReorderableStops } from './reorderable-stops'
import { TourDelete } from '../tour-delete'
import { StopCreate } from '../../stops/stop-create'
import { tourSchema } from '../../../schemas/tour'
import { actionCreators, GettyActionCreators } from '../../../actions'
import { GettyStore } from '../../../store'

// TODO copied from stops/index.tsx
const FilterButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: -${space(6)};
  margin-bottom: ${space(3)};
`;

const NoResults = styled.div`
  text-align: center;
  padding-top: ${space(5)};
  padding-bottom: ${space(5)};
`;

type DirectProps = {
  projectId: string;
  tourId: string;
  historyPush: (to: string) => void;
};

type ConnectedProps = Pick<GettyStore['getty'], 'tourUpdate' | 'stopCreate' | 'sites' | 'validationFailuresList'> & Pick<SpacesStore["spaces"], "spacesList">

type ActionProps =
  Pick<GettyActionCreators, 'gettyGetTourRequest' | 'gettyListStopsRequest' | 'gettyCreateStopReset' | 'gettyUpdateTourRequest' | 'gettyListSitesRequest' | 'gettyListValidationFailuresRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof spacesBindableActionCreators, "spacesRequest"> &
  Pick<SharedActionCreators, 'toastNotification'>;

type Props = ActionProps & DirectProps & ConnectedProps;
const validateTour = (validationRequest: GettyActionCreators['gettyListValidationFailuresRequest'], tourId: string): void => {
    validationRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [{
          typename: Tour.refName,
          id: tourId,
        }]
      }
    })
};

const TourUpdateView = ({
  gettyGetTourRequest,
  gettyUpdateTourRequest,
  historyPush,
  projectId,
  spacesList,
  spacesRequest,
  toastNotification,
  tourId,
  tourUpdate,
  gettyListSitesRequest,
  sites,
  validationFailuresList,
  gettyListValidationFailuresRequest,
  releaseControlsListReleaseControlsRequest,
  releaseControlsSetReleaseStatusRequest,
}: Props) => {
  const tourData = statusSelector.data(tourUpdate);
  const tour = tourData && tourData.tour;
  const stops = tour && tour.stops ? tour.stops : [];

  const tourRef = { typename: Tour.refName, id: tourId };

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteTourModalOpen, setDeleteTourModalOpen] = useState(false);
  const [newlyCreatedStopRef, setNewlyCreatedStopRef] = useState<Stop.Entity['ref']>();

  const spacesListData = statusSelector.data(spacesList)
  const sitesData = statusSelector.data(sites)
  const validationFailuresListData = statusSelector.data(validationFailuresList)

  const hasSubmitted = useRef(false)

  // {{{ keep page data up to date
  useEffect(() => {
    gettyGetTourRequest({
      type: GetTourRequest.refName,
      tourRef: { typename: Tour.refName, id: tourId },
    })
    releaseControlsListReleaseControlsRequest([{
      typename: Tour.refName,
      id: tourId
    }])
    gettyListSitesRequest()
    spacesRequest()
    validateTour(gettyListValidationFailuresRequest, tourId);
  }, [gettyGetTourRequest, tourId, spacesRequest, releaseControlsListReleaseControlsRequest, gettyListSitesRequest, gettyListValidationFailuresRequest]);
  // }}}

  // {{{ Intermediate state of re-ordered stops
  const [orderedStops, setOrderedStops] = useState<ReadonlyArray<Stop.Entity>>([]);

  useEffect(() => {
    if (tourUpdate.status === Status.Ready && stops.length && tour && Tour.mustRef(tour.ref).id === tourId) {
      setOrderedStops(stops)
    }

    if (tourUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([{
        typename: Tour.refName,
        id: tourId
      }])
      validateTour(gettyListValidationFailuresRequest, tourId);
      toastNotification({ type: 'success', text: 'Tour updated' })
      hasSubmitted.current = false
    }
  }, [stops, tourUpdate.status]) // eslint-disable-line react-hooks/exhaustive-deps
  // }}}

  const name = tour && tour.name ? tourSchema.name.read(tour.name) : '';

  const onStopCreate = (stop: Stop.Entity) => {
    releaseControlsListReleaseControlsRequest([{
      typename: Tour.refName,
      id: tourId
    }])
    setNewlyCreatedStopRef(stop.ref);
    setModalOpen(false);
    toastNotification({ type: 'success', text: '1 new stop added', durationMs: 2000 });
    gettyGetTourRequest({
      type: GetTourRequest.refName,
      tourRef: { typename: Tour.refName, id: tourId },
    });
    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [{
          typename: Tour.refName,
          id: tourId,
        }]
      }
    })
  }

  const updateTour = () => {
    const orderedStopRefs = orderedStops
      .map(stop => stop.ref)
      .filter(isNotNullOrUndefined);
    gettyUpdateTourRequest({
      ...UpdateTourRequest.defaults,
      updateMask: {
        type: "google.protobuf.FieldMask",
        fields: ['stopRefs'],
      },
      tourRef: tourRef,
      stopRefs: orderedStopRefs,
    });

    hasSubmitted.current = true
  }

  const modified = !stops.every((stop, i) => {
    const orderedStop = orderedStops[i];
    return orderedStop && orderedStop.ref && stop.ref && orderedStop.ref.id === stop.ref.id;
  });

  const deleteStopFromTour = (stopRef: Stop.Ref) => {
    const stops = orderedStops.filter(stop => stop.ref !== stopRef)
    setOrderedStops(stops);
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    tour && releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: tourRef,
      bundles,
    })
  }

  const failures = validationFailuresListData ? validationFailuresListData.find(({ ref }) => ref && ref.id === tourId) : undefined
  const failureFields = failures && failures.issues.find(({ issueType }: ValidationFailure.Issue.Entity) => issueType === 'TOUR_CONTAINS_STOPS_FROM_MULTIPLE_SITES' || issueType === 'TOUR_CONTAINS_NO_STOPS' || issueType === 'TOUR_CONTAINS_STOPS_AND_HAS_WEBPAGE_URL')
  const failureMessage = failureFields && IssueMesages[failureFields.issueType]

  return (
    <>
      {modalOpen && (
        <StopCreate
          onCancel={() => setModalOpen(false)}
          tourRefs={[tourRef]}
          onComplete={onStopCreate}
        />
      )}
      {deleteTourModalOpen && (
        <TourDelete
          onCancel={() => setDeleteTourModalOpen(false)}
          tourRef={tourRef}
          projectId={projectId}
          historyPush={historyPush}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit tour"
            onConfirm={modified ? () => historyPush(`/project/${projectId}/tours`) : undefined}
            navBackRoute={`/project/${projectId}/tours`}
          />
        )}
        title={typeof name === 'string' ? name : ''}
        toolbar={() => (
          <ButtonGroup>
            {tour &&
              <ReleaseControlUpdate
                hasBorder
                entityRef={tourRef}
                updateHandler={releaseControlUpdate}
              />
            }
            <PopOverMenu hasBorder={true}>
              <PopOverMenuButton onClick={() => setDeleteTourModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        subnav={() => (
          <SecondaryNavigation navItems={getTourSubnav(projectId, tourId)} modified={modified} historyPush={historyPush} >
            <Button variant="subtle" onClick={() => setModalOpen(true)}><IconPlus size="small" />
              <strong> create stop</strong>
            </Button>
          </SecondaryNavigation>
        )}
        summary={failureMessage ? () => failureMessage && <StatusSummary warning={[{ text: failureMessage }]} /> : undefined}
        content={() => (
          <>
            <StatusGuard status={tourUpdate}>
              <SectionHeader heading={`${orderedStops.length} ${pluralise(orderedStops.length, "stop", "stops")}`} />

              {!orderedStops.length ? (
                <NoResults>
                  <h3>No Stops</h3>
                  <p>This tour has no stops. Add or create some stops.</p>
                </NoResults>
              ) : (
                <ReorderableStops
                  historyPush={historyPush}
                  projectId={projectId}
                  orderedStops={orderedStops}
                  setOrderedStops={setOrderedStops}
                  tourId={tourId}
                  newlyCreatedStopRef={newlyCreatedStopRef}
                  deleteFromTour={deleteStopFromTour}
                  spacesListData={spacesListData}
                  sitesData={sitesData && sitesData.sites}
                />
              )}
            </StatusGuard>
          </>
        )}
        footer={() => (
          <FooterEditEntity
            entity={tourUpdate}
            modified={modified}
            onCancel={() => historyPush(`/project/${projectId}/stops`)}
            onSave={updateTour}
          />
        )}
      />
    </>
  )
}

export const TourUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, 'tourUpdate', 'stopCreate', 'sites', 'validationFailuresList'),
    ...pick(store.spaces, "spacesList"),
  }),
  {
    ...pick(actionCreators, 'gettyListStopsRequest', 'gettyGetTourRequest', 'gettyCreateStopReset', 'gettyUpdateTourRequest' , 'gettyListSitesRequest', 'gettyListValidationFailuresRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(spacesBindableActionCreators, "spacesRequest"),
    ...pick(sharedActionCreators, 'toastNotification'),
  },
)(TourUpdateView);
